import { Box, Flex } from '@chakra-ui/react';
import React, { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { getFromUrl } from '../../api/Api';
import useRangeChange from '../../hooks/useRangeChange';
import { CasesOverview } from '../../interface';
import { colors } from '../../theme/colors';
import {
  buildAnalyticsQuery,
  isAnalyticsQueryReady
} from '../../utils/dateRange';
import Card from '../Dashboard/Card';
import CardHeader from '../Dashboard/CardHeader';

const COLORS: Record<string, string> = {
  'Total Cases': colors.deep_blue,

  'Active Cases': colors.dark_green,

  'Closed Cases': colors.warning
};

const CasesOverviewChart = () => {
  const {
    range,
    startDate,
    endDate,
    handleChange,
    handleStartDateChange,
    handleEndDateChange
  } = useRangeChange();
  const [data, setData] = useState<{ name: string; value: number }[]>([]);

  React.useEffect(() => {
    const fetchData = async () => {
      if (!isAnalyticsQueryReady(range, startDate, endDate)) {
        setData([]);
        return;
      }

      try {
        const query = buildAnalyticsQuery(range, startDate, endDate);
        const [overview, numberOfCases] = await Promise.all([
          getFromUrl(`cases/metrics/comprehensive?${query}`),
          getFromUrl(`cases/number?${query}`)
        ]);
        const casesOverview = overview as CasesOverview;
        const chartData = [
          { name: 'Total Cases', value: numberOfCases?.data || 0 },

          {
            name: 'Active Cases',
            value: casesOverview?.total?.activeCases || 0
          },

          {
            name: 'Closed Cases',
            value: casesOverview?.total?.closedCases || 0
          }
        ];
        setData(chartData);
      } catch (e: any) {
        return;
      }
    };
    fetchData();
  }, [range, startDate, endDate]);

  return (
    <Card h='auto' py='24px' w='100%'>
      <Box width='100%'>
        <CardHeader
          title='Cases Overview'
          handleChange={handleChange}
          value={range}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
        />
        {data.length ? (
          <Box borderRadius='12px' margin='auto' my='14px'>
            <ResponsiveContainer width='100%' height={320}>
              <BarChart
                data={data}
                margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid
                  strokeDasharray='3 3'
                  stroke='#E2E8F0'
                  vertical={false}
                />
                <XAxis
                  dataKey='name'
                  tick={{ fontSize: 12, fill: colors.gray }}
                  strokeWidth={0}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: colors.gray }}
                  strokeWidth={0}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  contentStyle={{
                    backgroundColor: colors.white,
                    borderRadius: 8,
                    fontSize: '13px',
                    border: `1px solid ${colors.shade}`,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                />
                <Bar
                  dataKey='value'
                  barSize={40}
                  radius={[6, 6, 0, 0]}
                  fill={colors.deep_blue}
                  shape={(props: any) => {
                    const { x, y, width, height, name } = props;
                    return (
                      <rect
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        rx={6}
                        ry={6}
                        fill={COLORS[name] || colors.deep_blue}
                      />
                    );
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Flex
            justifyContent='center'
            marginX='auto'
            marginY='40px'
            width='100%'
            color={colors.gray}
          >
            {range === 'CUSTOM' && (!startDate || !endDate)
              ? 'Select start and end dates'
              : 'No data available'}
          </Flex>
        )}
      </Box>
    </Card>
  );
};

export default CasesOverviewChart;
